import { useEffect } from 'react';
import { useTracker } from './useTracker';

/** Canonical generic page-view event name. */
export const PAGE_VIEW = 'page_view';

/**
 * Fire a page-view track on mount, and again whenever `deps` change.
 *
 * Pass no `deps` for a single mount-time hit (the historical per-app behavior);
 * pass `[pathname]` (or your router's location key) to also track SPA route
 * changes — `usePageView(PAGE_VIEW, [location.pathname])`.
 *
 * Safe everywhere: it routes through `useTracker`, so it is a no-op when no
 * tracker is configured and never throws.
 *
 * @example
 * usePageView();                        // one mount-time page_view
 * usePageView("time_page_view");        // an app-namespaced event
 * usePageView(PAGE_VIEW, [pathname]);   // + on every route change
 */
export function usePageView(name: string = PAGE_VIEW, deps: readonly unknown[] = []): void {
  const { track } = useTracker();
  useEffect(() => {
    track({ name });
  }, [track, name, ...deps]);
}
